import type { ReviewParam } from "routes/Customer/Waste/Manifests/reviewParam";

import { type StatusChipColor } from "components/StatusChip";
import { gql } from "generated-graphql";
import {
  ManifestReviewDiscrepanciesQuery,
  StaffManifestReviewManifestsQuery,
} from "generated-graphql/graphql";

export const STAFF_MANIFEST_REVIEW_MANIFESTS_QUERY = gql(`
  query StaffManifestReviewManifests($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    staffManifestReviewManifests(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      totalCount
      items {
        id
        processedManifestId
        tenantId
        tenantName
        facilityName
        facilityEpaId
        manifestTrackingNumber
        discrepancyCount
        unresolvedDiscrepancyCount
        pendingDiscrepancyCount
        hasDocumentReview
        documentReviewPending
        availableSources
        status
        assignee {
          id
          email
          person {
            first
            last
          }
        }
        assignedBy {
          id
          email
          person {
            first
            last
          }
        }
        assignedAt
        documentIds
      }
    }
  }
`);

export const MANIFEST_REVIEW_DISCREPANCIES_QUERY = gql(`
  query ManifestReviewDiscrepancies($processedManifestId: ID!) {
    manifestDiscrepancies(processedManifestId: $processedManifestId) {
      id
      field
      status
      severity
      eManifestValue
      vendorValue
      manifestDocumentValue
    }
  }
`);

// Assignment is manifest-scoped: the server resolves each manifest's CURRENT active
// items (discrepancies + document review) at write time. Sending item ids captured at
// page load instead would fail whenever those items churned underneath an open tab
// (resolved elsewhere, EPA-correction rows deleted, re-seeds) — the "reassign does
// nothing, old assignee remains" bug.
export const ASSIGN_MANIFEST_REVIEW_MANIFESTS_MUTATION = gql(`
  mutation AssignManifestReviewManifestsFromQueue($processedManifestIds: [ID!]!, $staffUserId: ID) {
    assignManifestReviewManifests(processedManifestIds: $processedManifestIds, staffUserId: $staffUserId) {
      assignedDiscrepancyCount
      assignedDocumentReviewCount
    }
  }
`);

export type ManifestReviewRow =
  StaffManifestReviewManifestsQuery["staffManifestReviewManifests"]["items"][number];

export type ManifestReviewDiscrepancy =
  ManifestReviewDiscrepanciesQuery["manifestDiscrepancies"][number];

/** Active discrepancy: not yet resolved or dismissed (mirrors the queue's count). */
export function isActiveDiscrepancy(d: ManifestReviewDiscrepancy): boolean {
  return d.status !== "RESOLVED" && d.status !== "IGNORED";
}

/**
 * Destination for a queue row's Manifest # deep link. Document-review-first: a
 * manifest that has a document review opens the document review dialog;
 * otherwise (discrepancies-only) it opens the discrepancies panel. Resolved by
 * `ReviewPermalink` on the manifest detail route via the `?review=` param.
 *
 * Keyed on `hasDocumentReview` (the manifest has a needs-review document at all),
 * NOT `documentReviewPending` (which is true only while that document is
 * unassigned) — an already-assigned document review must still route to the
 * document dialog, not the (often empty) discrepancies panel.
 */
export function manifestReviewHref(
  row: Pick<ManifestReviewRow, "tenantId" | "processedManifestId" | "hasDocumentReview">
): string {
  const review: ReviewParam = row.hasDocumentReview ? "document" : "discrepancies";
  return `/o/${row.tenantId}/waste/manifests/${row.processedManifestId}?review=${review}`;
}

/**
 * Per-status chip styling for the Manifest Review queue. Colors group by the
 * two axes staff scan for:
 *   - unresolved discrepancies → orange (warning), escalating to red (error)
 *     once OVERDUE_60. "Unresolved" stays orange to match the queue's
 *     unresolved-count chip, the detail-dialog accents, and the schema
 *     `unresolvedDiscrepancyCount` ("orange circle") convention — one word,
 *     one color across the whole flow.
 *   - pending work → blue (pending discrepancies, info) and green (pending
 *     document review, success).
 * OVERDUE_45 and UNRESOLVED intentionally share orange (overdue is escalated-
 * unresolved); their labels disambiguate.
 */
export const STATUS_STYLES: Record<string, { label: string; color: StatusChipColor }> = {
  OVERDUE_60: { label: "Overdue 60+", color: "error" },
  OVERDUE_45: { label: "Overdue 45+", color: "warning" },
  UNRESOLVED: { label: "Unresolved", color: "warning" },
  PENDING: { label: "Pending", color: "info" },
  PENDING_REVIEW: { label: "Pending review", color: "success" },
};

/**
 * Chip style for a queue row status; unknown statuses fall back to a neutral
 * grey chip showing the raw value.
 */
export function statusChipStyle(status: string): { label: string; color: StatusChipColor } {
  return STATUS_STYLES[status] ?? { label: status, color: "default" };
}

/** Display name for an assignee (person name, falling back to email). */
export function assigneeLabel(assignee: ManifestReviewRow["assignee"]): string {
  if (!assignee) return "Unassigned";
  const name = assignee.person
    ? `${assignee.person.first ?? ""} ${assignee.person.last ?? ""}`.trim()
    : "";
  return name || assignee.email;
}
